import { useCallback } from 'react';
import type { CadFeatureMap } from '@mark43/cad-api';
import { useConnectionStatusDiagnostics } from '../connection-status/use-connection-status';
import type { ConnectionStatusHistoryEntry } from '../connection-status/connection-status-context';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';

/**
 * Diagnostic context gathered from the renderer process. Assembled and consumed
 * entirely in the renderer — never crosses the IPC bridge.
 */
export type RendererDiagnosticContext = {
    jsHeapUsedMb?: number;
    jsHeapTotalMb?: number;
    jsHeapLimitMb?: number;
    sessionDurationSeconds: number;
    hardwareConcurrency: number;
    deviceMemoryGb?: number;
    activeWebGLContextCount: number;
    totalCanvasCount: number;
    connectionStatus: Record<string, string>;
    connectionStatusHistory: ConnectionStatusHistoryEntry[];
    /** Snapshot of the client's enabled feature flags at collection time, so map
     * diagnostics can be segmented by which features (e.g. black-map fixes) are on. */
    enabledFeatures?: CadFeatureMap;
};

const pageLoadTimestamp = Date.now();

type PerformanceMemory = {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
};

function getPerformanceMemory(): PerformanceMemory | null {
    // Chrome-only API — not available in all browsers
    const perf = performance as { memory?: PerformanceMemory };
    return perf.memory ?? null;
}

function countWebGLContexts(): { active: number; total: number } {
    const canvases = Array.from(document.querySelectorAll('canvas'));
    let active = 0;
    for (const canvas of canvases) {
        try {
            const gl =
                canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false }) ??
                canvas.getContext('webgl', { failIfMajorPerformanceCaveat: false });
            if (gl && !gl.isContextLost()) {
                active++;
            }
        } catch {
            // Skip canvases that throw (e.g. 2d-only canvases)
        }
    }
    return { active, total: canvases.length };
}

/**
 * Returns a function that snapshots the renderer diagnostic context on demand.
 * Sources its own inputs (connection status, enabled features) via hooks so
 * callers don't have to thread them in, and reads the live values (heap memory,
 * WebGL context counts, session duration) at call time — so callers invoke the
 * returned function at the moment they need a snapshot (e.g. when a map fatal
 * error fires), not at render time.
 */
export function useRendererDiagnosticContext(): () => RendererDiagnosticContext {
    const { status, history } = useConnectionStatusDiagnostics();
    const enabledFeatures = useEnabledFeatures();

    return useCallback((): RendererDiagnosticContext => {
        const memory = getPerformanceMemory();
        const bytesToMb = (bytes: number) => Math.round((bytes / (1024 * 1024)) * 100) / 100;
        const webglCounts = countWebGLContexts();

        const result: RendererDiagnosticContext = {
            sessionDurationSeconds: Math.round((Date.now() - pageLoadTimestamp) / 1000),
            hardwareConcurrency: navigator.hardwareConcurrency ?? 0,
            activeWebGLContextCount: webglCounts.active,
            totalCanvasCount: webglCounts.total,
            connectionStatus: { ...status },
            connectionStatusHistory: [...history],
            enabledFeatures: { ...enabledFeatures },
        };

        if (memory) {
            result.jsHeapUsedMb = bytesToMb(memory.usedJSHeapSize);
            result.jsHeapTotalMb = bytesToMb(memory.totalJSHeapSize);
            result.jsHeapLimitMb = bytesToMb(memory.jsHeapSizeLimit);
        }

        const deviceMemory = (navigator as { deviceMemory?: number }).deviceMemory;
        if (deviceMemory != null) {
            result.deviceMemoryGb = deviceMemory;
        }

        return result;
    }, [status, history, enabledFeatures]);
}
